import React, { useContext, useCallback, useState, useEffect, useRef } from 'react';

import ConversationStarter from '~components/chat/conversation-starter';
import { useHistory } from 'react-router-dom';
import { useIntl } from 'react-intl';

import { useWebsocketEvent } from '~contexts/socket';
import Chat from '~components/chat';
import Button from '~components/button';

import { ChatContext } from '../context';
import { useChat } from '../useChat';
import { useTyping } from '../useTyping';
import { useConversationStarter } from '../useConversationStarter';

const IS_WRITING_TIMEOUT = 5000;
// Threshold is 24 h expressed in seconds to match the Unix epoch unit of
// `discussion.acceptedAt` (sourced from the server's `accepted_at` field).
// We still compare against Date.now() because the API does not expose a
// server-current-time value; a future `is_reminder_due` flag from the server
// would remove this dependency on the client clock entirely.
const REMINDER_THRESHOLD_SECONDS = 24 * 60 * 60;

const ChatMain = ({ perPage }) => {
  const { recipient, currentUser, discussion, messages, onNotInterested } = useContext(ChatContext);
  const intl = useIntl();
  const history = useHistory();

  const {
    isLoadingMessages,
    sendMessage,
    fetchNewMessages,
    loadMoreOldMessages,
    scrollTo,
    forceScroll,
    seenByPeerAt,
    handleSeenByPerson,
  } = useChat({ perPage });

  const { markAsTyping, isPeerWriting, setIsPeerWriting, setIsPeerNotWriting } = useTyping({
    discussionId: discussion.discussionId,
    personId: currentUser.personId,
    recipientId: recipient.personId,
    writingTime: IS_WRITING_TIMEOUT,
  });

  const {
    variants: starterVariants,
    isLoading: isLoadingStarter,
    refreshCount,
    isDismissed,
    fetchStarter,
    refreshStarter,
    dismissStarter,
  } = useConversationStarter();

  // Text pre-filled from a conversation starter suggestion
  const [prefillMessage, setPrefillMessage] = useState('');

  const handleNewMessage = useCallback(
    (payload) => {
      // Always fetch messages when we receive a 'sent' event, regardless of other conditions
      if (payload && payload.chat_id && discussion.discussionId === payload.chat_id) {
        setIsPeerNotWriting();
        fetchNewMessages();
        return;
      }

      // If we don't have a discussion ID yet but received a message for this recipient
      if (!discussion.discussionId && payload && payload.participant_id === recipient.personId) {
        fetchNewMessages();
      }
    },
    [discussion.discussionId, recipient.personId, fetchNewMessages, setIsPeerNotWriting]
  );

  const handlePeerWriting = useCallback((payload) => {
    if (payload && discussion.discussionId === payload.chat_id) {
      setIsPeerWriting();
    }
  });


  const isAccountDeleted =
    discussion.participants.recipient.isDeleted === true ||
    discussion.participants.recipient.firstName === '[account-deleted]';

  const isSendMessageDisabled =
    discussion.participants.currentUser.hasBlockedChat ||
    discussion.participants.recipient.hasBlockedChat ||
    isAccountDeleted ||
    discussion.isRejected ||
    discussion.acceptanceState === 'rejected';

  // Show conversation starter only when there are no messages, chat is not disabled, and not dismissed
  const showConversationStarter =
    !isLoadingMessages && messages.length === 0 && !isSendMessageDisabled && !isAccountDeleted && !isDismissed;



  const handleStarterSelect = useCallback((text) => {
    setPrefillMessage(text);
  }, []);

  // Clear the prefill after it's been consumed (to allow re-selecting the same message)
  const handlePrefillConsumed = useCallback(() => {
    setPrefillMessage('');
  }, []);

  useWebsocketEvent('seen', handleSeenByPerson);
  useWebsocketEvent('typing', handlePeerWriting);
  useWebsocketEvent('sent', handleNewMessage);

  const hasWorkerSentMessage = messages.some((m) => m.personId === (currentUser.accountType === 'worker' ? currentUser.personId : recipient.personId));
  const isWorker = currentUser.accountType === 'worker';
  const isCustomer = currentUser.accountType === 'client';

  // 3. Not Interested button inside chat conditions
  const showNotInterestedButton =
    isWorker &&
    discussion.acceptanceState === 'accepted' &&
    !discussion.isRejected &&
    !hasWorkerSentMessage;

  // 4. Customer view when worker marked Not Interested
  const isRejectedNotInterested =
    discussion.isRejected && discussion.rejectionReason === 'not_interested';
  const showDeclinedBanner = isCustomer && isRejectedNotInterested;

  // 5. Worker still hasn't replied reminder
  const isAfterReminderPeriod =
    discussion.acceptedAt &&
    Math.floor(Date.now() / 1000) > discussion.acceptedAt + REMINDER_THRESHOLD_SECONDS;
  const showReminderMessage =
    isCustomer &&
    discussion.acceptanceState === 'accepted' &&
    !discussion.isRejected &&
    !hasWorkerSentMessage &&
    isAfterReminderPeriod;

  const locationId = currentUser?.wish?.address?.place_id || recipient?.location?.locationId || '';
  const searchOtherWorkersPath = locationId ? `/workers?locationId=${locationId}` : '/workers';

  const footerRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const footerEl = footerRef.current;
    if (!footerEl) return;

    const updateHeight = () => {
      const height = footerEl.offsetHeight;
      if (wrapperRef.current) {
        wrapperRef.current.style.setProperty('--chat-input-height', `${height}px`);
      }
    };

    const observer = new ResizeObserver(updateHeight);
    observer.observe(footerEl);
    updateHeight();

    return () => observer.disconnect();
  }, []);

  return (
    <Chat.Wrapper ref={wrapperRef} className="c-chat-page__main">
      {showNotInterestedButton && (
        <div className="c-chat-banner c-chat-banner--not-interested">
          <span>
            {intl.formatMessage({
              id: 'containers.discussion.not-interested.banner-text',
              defaultMessage: 'Not interested in this customer?',
            })}
          </span>
          <Button onClick={onNotInterested} className="c-btn c-btn--secondary">
            {intl.formatMessage({
              id: 'containers.discussion.not-interested.button',
              defaultMessage: 'Not Interested',
            })}
          </Button>
        </div>
      )}

      {showDeclinedBanner && (
        <div className="c-chat-banner c-chat-banner--rejected">
          <p>
            {intl.formatMessage(
              {
                id: 'containers.discussion.customer-rejected-banner',
                defaultMessage: '{workerName} is not interested. Try contacting other workers.',
              },
              { workerName: recipient.firstName }
            )}
          </p>
          <Button onClick={() => history.push(searchOtherWorkersPath)} className="c-btn c-btn--primary">
            {intl.formatMessage({
              id: 'containers.discussion.search-other-workers',
              defaultMessage: 'Search for other workers',
            })}
          </Button>
        </div>
      )}

      {showReminderMessage && (
        <div className="c-chat-banner c-chat-banner--reminder">
          <p>
            {intl.formatMessage({
              id: 'containers.discussion.worker-no-reply-reminder',
              defaultMessage:
                'It seems the worker is taking some time to reply. You can try contacting other workers in the meantime.',
            })}
          </p>
          <Button onClick={() => history.push(searchOtherWorkersPath)} className="c-btn c-btn--secondary">
            {intl.formatMessage({
              id: 'containers.discussion.search-other-workers-reminder',
              defaultMessage: 'Search for other workers',
            })}
          </Button>
        </div>
      )}

      <Chat.Messages
        participants={discussion.participants}
        messages={messages}
        createdAt={discussion.createdAt}
        isLoading={isLoadingMessages}
        onScrollEnd={loadMoreOldMessages}
        scrollTo={scrollTo}
        forceScroll={forceScroll}
        isPeerWriting={isPeerWriting}
        peerId={recipient.personId}
        seenByPeerAt={seenByPeerAt}
        personId={currentUser.personId}
        className="c-chat-page__content"
        discussion={discussion}
        recipient={recipient}
      />
      {showConversationStarter && (
        <ConversationStarter
          variants={starterVariants}
          isLoading={isLoadingStarter}
          refreshCount={refreshCount}
          onRefresh={refreshStarter}
          onSelect={handleStarterSelect}
          onDismiss={dismissStarter}
          fetchStarter={fetchStarter}
        />
      )}
      <div ref={footerRef} className="c-chat-page__footer">
        {isAccountDeleted && (
          <div className="c-chat-page__deleted-banner" role="alert">
            {intl.formatMessage({
              id: 'components.chat.account-deleted-banner',
              defaultMessage: 'This user has deleted their account. You can no longer send messages.',
            })}
          </div>
        )}
        {!isAccountDeleted && (
          <Chat.Form
          onSubmit={sendMessage}
          onType={markAsTyping}
          isDisabled={isSendMessageDisabled}
          isRecipientAccountDeleted={isAccountDeleted}
          prefillMessage={prefillMessage}
          onPrefillConsumed={handlePrefillConsumed}
          isRejected={discussion.isRejected || discussion.acceptanceState === 'rejected'}
        />
        )}
        
      </div>
    </Chat.Wrapper>
  );
};

export default ChatMain;
